import React, { useState, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { X, ChevronLeft, ChevronRight, Download, ZoomIn, ZoomOut, Maximize2, Minimize2, RotateCw, HelpCircle } from 'lucide-react';
import { EPaperViewerProps } from '../types/epaper';
import './EPaperViewer.css';

// Set up PDF.js worker - use unpkg CDN which is more reliable
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const EPaperViewer: React.FC<EPaperViewerProps> = ({ epaper, onClose }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [fullscreen, setFullscreen] = useState<boolean>(false);
  const [showHelp, setShowHelp] = useState<boolean>(false);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const changePage = useCallback((offset: number) => {
    setPageNumber(prevPageNumber => {
      const newPageNumber = prevPageNumber + offset;
      return Math.min(Math.max(1, newPageNumber), numPages);
    });
  }, [numPages]);

  const changeScale = (newScale: number) => {
    setScale(Math.min(Math.max(0.5, newScale), 3.0));
  };

  const toggleFullscreen = useCallback(() => {
    setFullscreen(!fullscreen);
  }, [fullscreen]);

  const resetView = () => {
    setScale(1.0);
    setPageNumber(1);
  };

  // Keyboard shortcuts and mobile touch gestures
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!epaper) return;
      
      switch (event.key) {
        case 'Escape':
          if (fullscreen) {
            setFullscreen(false);
          } else {
            onClose();
          }
          break;
        case 'ArrowLeft':
          if (numPages > 1) changePage(-1);
          break;
        case 'ArrowRight':
          if (numPages > 1) changePage(1);
          break;
        case '+':
        case '=':
          changeScale(scale + 0.2);
          break;
        case '-':
          changeScale(scale - 0.2);
          break;
        case '0':
          resetView();
          break;
        case 'f':
          toggleFullscreen();
          break;
      }
    };

    // Mobile touch gesture handling
    let touchStartX = 0;
    let touchStartY = 0;
    let touchEndX = 0;
    let touchEndY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.changedTouches[0].screenX;
      touchStartY = e.changedTouches[0].screenY;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      touchEndX = e.changedTouches[0].screenX;
      touchEndY = e.changedTouches[0].screenY;
      handleSwipe();
    };

    const handleSwipe = () => {
      const diffX = touchStartX - touchEndX;
      const diffY = touchStartY - touchEndY;
      const minSwipeDistance = 50;

      // Horizontal swipe for page navigation
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > minSwipeDistance) {
        if (diffX > 0 && numPages > 1) {
          // Swipe left - next page
          changePage(1);
        } else if (diffX < 0 && numPages > 1) {
          // Swipe right - previous page
          changePage(-1);
        }
      }

      // Vertical swipe for zoom (if implemented)
      if (Math.abs(diffY) > Math.abs(diffX) && Math.abs(diffY) > minSwipeDistance) {
        if (diffY > 0) {
          // Swipe up - zoom in
          changeScale(scale + 0.1);
        } else if (diffY < 0) {
          // Swipe down - zoom out
          changeScale(scale - 0.1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [epaper, fullscreen, numPages, scale, onClose, changePage, toggleFullscreen]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!epaper) return null;

  return (
    <div className={`epaper-viewer-overlay ${fullscreen ? 'fullscreen' : ''}`} onClick={onClose}>
      <div className={`epaper-viewer-container ${fullscreen ? 'fullscreen' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="epaper-viewer-header">
          <div className="epaper-viewer-info">
            <h2>{epaper.title}</h2>
            <p>{formatDate(epaper.date)}</p>
          </div>
          
          <div className="epaper-viewer-controls">
            <button 
              className="control-button"
              onClick={() => changeScale(scale - 0.2)}
              disabled={scale <= 0.5}
            >
              <ZoomOut size={20} />
            </button>
            
            <span className="scale-display">{Math.round(scale * 100)}%</span>
            
            <button 
              className="control-button"
              onClick={() => changeScale(scale + 0.2)}
              disabled={scale >= 3.0}
            >
              <ZoomIn size={20} />
            </button>
            
            <button 
              className="control-button"
              onClick={resetView}
              title="Reset view"
            >
              <RotateCw size={20} />
            </button>
            
            <button 
              className="control-button"
              onClick={toggleFullscreen}
              title={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              {fullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
            </button>
            
            <button 
              className="control-button" 
              onClick={() => window.open(epaper.pdfUrl, '_blank')}
              title="Download PDF"
            >
              <Download size={20} />
            </button>
            
            <button 
              className="control-button"
              onClick={() => setShowHelp(!showHelp)}
              title="Keyboard shortcuts"
            >
              <HelpCircle size={20} />
            </button>
            
            <button className="control-button close-button" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="epaper-viewer-content">
          <Document
            file={epaper.pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) => {
              console.error('PDF load error:', error);
            }}
            loading={
              <div className="pdf-loading">
                <div className="spinner"></div>
                <p>Loading PDF...</p>
              </div>
            }
            error={
              <div className="pdf-error">
                <p>Failed to load PDF. Please try the download button instead.</p>
                <button 
                  className="download-button"
                  onClick={() => window.open(epaper.pdfUrl, '_blank')}
                >
                  Download PDF
                </button>
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              scale={scale}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="pdf-page"
            />
          </Document>
          
          {/* Mobile touch hints */}
          <div className="mobile-touch-hints">
            <div className="touch-hint">
              <span>👆 Swipe left/right to navigate</span>
            </div>
            <div className="touch-hint">
              <span>👆 Swipe up/down to zoom</span>
            </div>
          </div>
        </div>

        {numPages > 1 && (
          <div className="epaper-viewer-footer">
            <button 
              className="page-button"
              onClick={() => changePage(-1)}
              disabled={pageNumber <= 1}
            >
              <ChevronLeft size={20} />
            </button>
            
            <span className="page-info">
              Page {pageNumber} of {numPages}
            </span>
            
            <button 
              className="page-button"
              onClick={() => changePage(1)}
              disabled={pageNumber >= numPages}
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>
      
      {showHelp && (
        <div className="help-overlay" onClick={() => setShowHelp(false)}>
          <div className="help-content" onClick={(e) => e.stopPropagation()}>
            <h3>Keyboard Shortcuts</h3>
            <div className="shortcuts-list">
              <div className="shortcut-item">
                <kbd>←</kbd> <span>Previous page</span>
              </div>
              <div className="shortcut-item">
                <kbd>→</kbd> <span>Next page</span>
              </div>
              <div className="shortcut-item">
                <kbd>+</kbd> <span>Zoom in</span>
              </div>
              <div className="shortcut-item">
                <kbd>-</kbd> <span>Zoom out</span>
              </div>
              <div className="shortcut-item">
                <kbd>0</kbd> <span>Reset view</span>
              </div>
              <div className="shortcut-item">
                <kbd>f</kbd> <span>Toggle fullscreen</span>
              </div>
              <div className="shortcut-item">
                <kbd>Esc</kbd> <span>Close / Exit fullscreen</span>
              </div>
            </div>
            <button className="help-close" onClick={() => setShowHelp(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EPaperViewer; 